import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { asignacionesAPI } from '../services/api';
import { Car, Plus, ArrowLeft, Calendar, User, Upload, PenTool, Mail, CheckCircle, FileText, Loader2, Edit2 } from 'lucide-react';
import ImportadorAsignaciones from '../components/ImportadorAsignaciones';
import ModalFirma from '../components/ModalFirma';
import { useAuth } from '../contexts/AuthContext';

function Asignaciones() {
  const [asignaciones, setAsignaciones] = useState([]);
  const [firmasEstado, setFirmasEstado] = useState({});
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarImportador, setMostrarImportador] = useState(false);
  const [editandoAsignacion, setEditandoAsignacion] = useState(null);
  const [modalFirma, setModalFirma] = useState(null);
  const [enviandoEmail, setEnviandoEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();
  const empresa = usuario?.empresa || {};

  useEffect(() => {
    if (authLoading) return;
    if (!empresa.id) {
      navigate('/');
      return;
    }
    cargarAsignaciones();
  }, [authLoading, usuario]);

  const cargarAsignaciones = async () => {
    try {
      const response = await asignacionesAPI.listar({ empresa_id: empresa.id });
      const asignacionesList = response.data.data || [];
      setAsignaciones(asignacionesList);

      // Cargar estado de firmas para cada asignación
      const estados = {};
      await Promise.all(
        asignacionesList.map(async (a) => {
          try {
            const firmaResponse = await api.get(`/firmas/asignacion/${a.id}`);
            estados[a.id] = firmaResponse.data.data;
          } catch (err) {
            estados[a.id] = { estado_custodia: 'PENDIENTE', tiene_firma_entrega: false, tiene_firma_devolucion: false };
          }
        })
      );
      setFirmasEstado(estados);
    } catch (error) {
      console.error('Error cargando asignaciones:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNuevaAsignacion = async (data) => {
    try {
      await asignacionesAPI.crear({ ...data, empresa_id: empresa.id });
      setMostrarFormulario(false);
      cargarAsignaciones();
    } catch (error) {
      console.error('Error creando asignación:', error);
      const mensaje = error.response?.data?.message || error.response?.data?.error || 'Error al crear asignación';
      alert(mensaje);
    }
  };

  const handleEditarAsignacion = async (data) => {
    try {
      await asignacionesAPI.actualizar(editandoAsignacion.id, data);
      setEditandoAsignacion(null);
      cargarAsignaciones();
    } catch (error) {
      console.error('Error actualizando asignación:', error);
      const mensaje = error.response?.data?.message || error.response?.data?.error || 'Error al actualizar asignación';
      alert(mensaje);
    }
  };

  const enviarEnlaceFirma = async (asignacionId, tipoFirma) => {
    setEnviandoEmail(asignacionId + tipoFirma);
    try {
      const endpoint = tipoFirma === 'ENTREGA'
        ? `/firmas/solicitar-entrega/${asignacionId}`
        : `/firmas/solicitar-devolucion/${asignacionId}`;

      await api.post(endpoint);
      alert('Enlace de firma enviado por email');
      cargarAsignaciones();
    } catch (error) {
      alert(error.response?.data?.error || 'Error al enviar enlace');
    } finally {
      setEnviandoEmail(null);
    }
  };

  const descargarDocumento = async (firmaId) => {
    try {
      const response = await api.get(`/firmas/documento/${firmaId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `custodia_${firmaId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Error al descargar documento');
    }
  };

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'COMPLETADO':
        return 'bg-green-100 text-green-700';
      case 'EN_CUSTODIA':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getEstadoTexto = (estado) => {
    switch (estado) {
      case 'COMPLETADO':
        return 'Completado';
      case 'EN_CUSTODIA':
        return 'En Custodia';
      default:
        return 'Pendiente';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Asignaciones Temporales</h1>
              <p className="text-sm text-gray-600">{empresa.nombre}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMostrarImportador(true)}
              className="flex items-center gap-2 border border-blue-600 text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50"
            >
              <Upload className="w-5 h-5" />
              Importar CSV/Excel
            </button>
            <button
              onClick={() => setMostrarFormulario(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5" />
              Nueva Asignación
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="text-center py-12">Cargando...</div>
        ) : asignaciones.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay asignaciones
            </h3>
            <p className="text-gray-600 mb-4">
              Crea tu primera asignación temporal
            </p>
            <button
              onClick={() => setMostrarFormulario(true)}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Crear Asignación
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {asignaciones.map((asignacion) => {
              const firmaEstado = firmasEstado[asignacion.id] || {};
              const estadoCustodia = firmaEstado.estado_custodia || 'PENDIENTE';
              const tieneEntrega = firmaEstado.tiene_firma_entrega;
              const tieneDevolucion = firmaEstado.tiene_firma_devolucion;
              const tieneEmail = asignacion.conductor_email;

              return (
                <div key={asignacion.id} className="bg-white p-6 rounded-xl shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-blue-100 p-3 rounded-lg">
                          <Car className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">
                            {asignacion.matricula}
                          </h3>
                          <p className="text-sm text-gray-600">{asignacion.motivo || 'Vehículo sustitución'}</p>
                        </div>
                        {/* Badge de estado de custodia */}
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getEstadoBadge(estadoCustodia)}`}>
                          {getEstadoTexto(estadoCustodia)}
                        </span>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center gap-2 text-gray-600 mb-1">
                            <User className="w-4 h-4" />
                            <span className="text-sm font-medium">Conductor</span>
                          </div>
                          <p className="text-gray-900">{asignacion.conductor_nombre} {asignacion.conductor_apellidos}</p>
                          <p className="text-sm text-gray-600">DNI: {asignacion.conductor_dni}</p>
                          {asignacion.conductor_direccion && (
                            <p className="text-sm text-gray-500">
                              {asignacion.conductor_direccion}
                              {asignacion.conductor_codigo_postal && `, ${asignacion.conductor_codigo_postal}`}
                              {asignacion.conductor_ciudad && ` ${asignacion.conductor_ciudad}`}
                              {asignacion.conductor_provincia && ` (${asignacion.conductor_provincia})`}
                            </p>
                          )}
                          {tieneEmail && (
                            <p className="text-sm text-gray-500">{asignacion.conductor_email}</p>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 text-gray-600 mb-1">
                            <Calendar className="w-4 h-4" />
                            <span className="text-sm font-medium">Período de Custodia</span>
                          </div>
                          {tieneEntrega ? (
                            <div className="space-y-1">
                              <p className="text-sm">
                                <span className="text-gray-500">Entrega:</span>{' '}
                                <span className="text-gray-900">
                                  {firmaEstado.firma_entrega_timestamp
                                    ? new Date(firmaEstado.firma_entrega_timestamp).toLocaleString('es-ES')
                                    : 'Firmado'}
                                </span>
                              </p>
                              {tieneDevolucion ? (
                                <p className="text-sm">
                                  <span className="text-gray-500">Devolución:</span>{' '}
                                  <span className="text-gray-900">
                                    {firmaEstado.firma_devolucion_timestamp
                                      ? new Date(firmaEstado.firma_devolucion_timestamp).toLocaleString('es-ES')
                                      : 'Firmado'}
                                  </span>
                                </p>
                              ) : (
                                <p className="text-sm text-yellow-600">En custodia del conductor</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-gray-500 italic">Pendiente de firma de entrega</p>
                          )}
                        </div>
                      </div>

                      {/* Estado de firmas */}
                      <div className="mt-4 pt-4 border-t flex items-center gap-4 flex-wrap">
                        {/* Firma Entrega */}
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${tieneEntrega ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span className="text-sm text-gray-600">Firma Entrega</span>
                          {tieneEntrega && firmaEstado.firma_entrega_id && (
                            <button
                              onClick={() => descargarDocumento(firmaEstado.firma_entrega_id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Descargar PDF"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        {/* Firma Devolución */}
                        <div className="flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${tieneDevolucion ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                          <span className="text-sm text-gray-600">Firma Devolución</span>
                          {tieneDevolucion && firmaEstado.firma_devolucion_id && (
                            <button
                              onClick={() => descargarDocumento(firmaEstado.firma_devolucion_id)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Descargar PDF"
                            >
                              <FileText className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Botones de acción de firma */}
                      <div className="mt-4 flex flex-wrap gap-2">
                        {/* Botones de Entrega */}
                        {!tieneEntrega && (
                          <>
                            <button
                              onClick={() => setModalFirma({ asignacion: { ...asignacion, ...firmaEstado }, tipoFirma: 'ENTREGA' })}
                              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                            >
                              <PenTool className="w-4 h-4" />
                              Firmar Entrega
                            </button>
                            {tieneEmail && (
                              <button
                                onClick={() => enviarEnlaceFirma(asignacion.id, 'ENTREGA')}
                                disabled={enviandoEmail === asignacion.id + 'ENTREGA'}
                                className="flex items-center gap-1 px-3 py-1.5 border border-green-600 text-green-600 text-sm rounded-lg hover:bg-green-50 disabled:opacity-50"
                              >
                                {enviandoEmail === asignacion.id + 'ENTREGA' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                                Enviar enlace
                              </button>
                            )}
                          </>
                        )}

                        {/* Botones de Devolución (solo si ya hay entrega) */}
                        {tieneEntrega && !tieneDevolucion && (
                          <>
                            <button
                              onClick={() => setModalFirma({ asignacion: { ...asignacion, ...firmaEstado }, tipoFirma: 'DEVOLUCION' })}
                              className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                            >
                              <PenTool className="w-4 h-4" />
                              Firmar Devolución
                            </button>
                            {tieneEmail && (
                              <button
                                onClick={() => enviarEnlaceFirma(asignacion.id, 'DEVOLUCION')}
                                disabled={enviandoEmail === asignacion.id + 'DEVOLUCION'}
                                className="flex items-center gap-1 px-3 py-1.5 border border-red-600 text-red-600 text-sm rounded-lg hover:bg-red-50 disabled:opacity-50"
                              >
                                {enviandoEmail === asignacion.id + 'DEVOLUCION' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mail className="w-4 h-4" />
                                )}
                                Enviar enlace
                              </button>
                            )}
                          </>
                        )}

                        {/* Estado completado */}
                        {tieneEntrega && tieneDevolucion && (
                          <span className="flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 text-sm rounded-lg">
                            <CheckCircle className="w-4 h-4" />
                            Custodia Completada
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        asignacion.activo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {asignacion.activo ? 'Activa' : 'Inactiva'}
                      </span>
                      {asignacion.activo && (
                        <button
                          onClick={() => setEditandoAsignacion(asignacion)}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <Edit2 className="w-4 h-4" />
                          Editar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {mostrarFormulario && (
        <FormularioAsignacion
          onSubmit={handleNuevaAsignacion}
          onCancel={() => setMostrarFormulario(false)}
        />
      )}

      {editandoAsignacion && (
        <FormularioAsignacion
          initialData={editandoAsignacion}
          onSubmit={handleEditarAsignacion}
          onCancel={() => setEditandoAsignacion(null)}
        />
      )}

      {mostrarImportador && (
        <ImportadorAsignaciones
          onClose={() => setMostrarImportador(false)}
          onImportComplete={cargarAsignaciones}
        />
      )}

      {modalFirma && (
        <ModalFirma
          asignacion={modalFirma.asignacion}
          tipoFirma={modalFirma.tipoFirma}
          onClose={() => setModalFirma(null)}
          onSuccess={cargarAsignaciones}
        />
      )}
    </div>
  );
}

/**
 * Valida un documento de identidad español (DNI/NIE) o pasaporte
 */
function validarDocumentoIdentidad(documento) {
  if (!documento || typeof documento !== 'string') {
    return { valido: false, tipo: null, mensaje: 'Documento requerido' };
  }

  const doc = documento.replace(/[\s\-]/g, '').toUpperCase();
  const regexDNI = /^(\d{8})([A-Z])$/;
  const regexNIE = /^([XYZ])(\d{7})([A-Z])$/;
  const regexPasaporteES = /^[A-Z]{3}\d{6}$/;
  const regexPasaporteGenerico = /^[A-Z0-9]{6,12}$/;
  const letrasControl = 'TRWAGMYFPDXBNJZSQVHLCKE';

  // Validar DNI
  const matchDNI = doc.match(regexDNI);
  if (matchDNI) {
    const numero = parseInt(matchDNI[1], 10);
    const letraEsperada = letrasControl[numero % 23];
    if (letraEsperada === matchDNI[2]) {
      return { valido: true, tipo: 'DNI', mensaje: 'DNI válido' };
    }
    return { valido: false, tipo: 'DNI', mensaje: `Letra incorrecta, debería ser ${letraEsperada}` };
  }

  // Validar NIE
  const matchNIE = doc.match(regexNIE);
  if (matchNIE) {
    const numeroInicial = { 'X': '0', 'Y': '1', 'Z': '2' }[matchNIE[1]];
    const numeroCompleto = parseInt(numeroInicial + matchNIE[2], 10);
    const letraEsperada = letrasControl[numeroCompleto % 23];
    if (letraEsperada === matchNIE[3]) {
      return { valido: true, tipo: 'NIE', mensaje: 'NIE válido' };
    }
    return { valido: false, tipo: 'NIE', mensaje: `Letra incorrecta, debería ser ${letraEsperada}` };
  }

  // Validar Pasaporte
  if (regexPasaporteES.test(doc) || (regexPasaporteGenerico.test(doc) && !/^\d+$/.test(doc))) {
    return { valido: true, tipo: 'Pasaporte', mensaje: 'Pasaporte válido' };
  }

  return { valido: false, tipo: null, mensaje: 'Formato no válido (DNI, NIE o Pasaporte)' };
}

/**
 * Valida un email
 */
function validarEmail(email) {
  if (!email || typeof email !== 'string') {
    return { valido: false, mensaje: 'Email requerido' };
  }
  const emailLimpio = email.trim().toLowerCase();
  const regexEmail = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

  if (!regexEmail.test(emailLimpio)) {
    return { valido: false, mensaje: 'Formato de email inválido' };
  }

  const dominiosMalEscritos = {
    'gmial.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gamil.com': 'gmail.com',
    'hotmal.com': 'hotmail.com', 'hotmai.com': 'hotmail.com',
    'outloo.com': 'outlook.com', 'outlok.com': 'outlook.com',
  };
  const dominio = emailLimpio.split('@')[1];
  if (dominiosMalEscritos[dominio]) {
    return { valido: false, mensaje: `¿Quisiste decir @${dominiosMalEscritos[dominio]}?` };
  }
  return { valido: true, mensaje: 'Email válido' };
}

/**
 * Valida un teléfono español
 */
function validarTelefono(telefono) {
  if (!telefono || typeof telefono !== 'string') {
    return { valido: false, tipo: null, mensaje: 'Teléfono requerido' };
  }
  const tel = telefono.replace(/[\s\-\(\)\.]/g, '');

  if (/^[67]\d{8}$/.test(tel)) return { valido: true, tipo: 'Móvil', mensaje: 'Móvil válido' };
  if (/^[89]\d{8}$/.test(tel)) return { valido: true, tipo: 'Fijo', mensaje: 'Fijo válido' };
  if (/^\+34[6789]\d{8}$/.test(tel)) return { valido: true, tipo: 'Móvil', mensaje: 'Válido' };
  if (/^\+\d{2,3}\d{6,14}$/.test(tel)) return { valido: true, tipo: 'Internacional', mensaje: 'Internacional válido' };

  return { valido: false, tipo: null, mensaje: 'Formato: 612345678 o +34612345678' };
}

const TIPOS_VIA = [
  'CALLE', 'AVENIDA', 'PASEO', 'PLAZA', 'CAMINO', 'CARRETERA',
  'RONDA', 'TRAVESIA', 'URBANIZACION', 'POLIGONO', 'GLORIETA',
  'RAMBLA', 'BOULEVARD', 'ACCESO', 'ALAMEDA', 'BARRIO', 'COSTANILLA',
  'PASAJE', 'SENDA', 'VIA',
];

function FormularioAsignacion({ onSubmit, onCancel, initialData }) {
  const isEditing = !!initialData;

  const [formData, setFormData] = useState(() => {
    if (initialData) {
      return {
        matricula: initialData.matricula || '',
        conductor_dni: initialData.conductor_dni || '',
        conductor_nombre: initialData.conductor_nombre || '',
        conductor_apellidos: initialData.conductor_apellidos || '',
        conductor_email: initialData.conductor_email || '',
        conductor_telefono: initialData.conductor_telefono || '',
        conductor_tipo_via: initialData.conductor_tipo_via || '',
        conductor_nombre_via: initialData.conductor_nombre_via || '',
        conductor_numero: initialData.conductor_numero || '',
        conductor_portal: initialData.conductor_portal || '',
        conductor_escalera: initialData.conductor_escalera || '',
        conductor_planta: initialData.conductor_planta || '',
        conductor_puerta: initialData.conductor_puerta || '',
        conductor_codigo_postal: initialData.conductor_codigo_postal || '',
        conductor_poblacion: initialData.conductor_poblacion || initialData.conductor_ciudad || '',
        conductor_provincia: initialData.conductor_provincia || '',
        motivo: initialData.motivo || 'vehículo_sustitución',
      };
    }
    return {
      matricula: '',
      conductor_dni: '',
      conductor_nombre: '',
      conductor_apellidos: '',
      conductor_email: '',
      conductor_telefono: '',
      conductor_tipo_via: '',
      conductor_nombre_via: '',
      conductor_numero: '',
      conductor_portal: '',
      conductor_escalera: '',
      conductor_planta: '',
      conductor_puerta: '',
      conductor_codigo_postal: '',
      conductor_poblacion: '',
      conductor_provincia: '',
      motivo: 'vehículo_sustitución',
    };
  });
  const [dniError, setDniError] = useState('');
  const [dniTipo, setDniTipo] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailValido, setEmailValido] = useState(false);
  const [telefonoError, setTelefonoError] = useState('');
  const [telefonoTipo, setTelefonoTipo] = useState('');
  const [cpError, setCpError] = useState('');

  const handleDniChange = (e) => {
    const valor = e.target.value.toUpperCase();
    setFormData({...formData, conductor_dni: valor});

    if (valor.length >= 8) {
      const validacion = validarDocumentoIdentidad(valor);
      if (validacion.valido) {
        setDniError('');
        setDniTipo(validacion.tipo);
      } else {
        setDniError(validacion.mensaje);
        setDniTipo('');
      }
    } else {
      setDniError('');
      setDniTipo('');
    }
  };

  const handleEmailChange = (e) => {
    const valor = e.target.value;
    setFormData({...formData, conductor_email: valor});

    if (valor.length >= 5 && valor.includes('@')) {
      const validacion = validarEmail(valor);
      if (validacion.valido) {
        setEmailError('');
        setEmailValido(true);
      } else {
        setEmailError(validacion.mensaje);
        setEmailValido(false);
      }
    } else {
      setEmailError('');
      setEmailValido(false);
    }
  };

  const handleTelefonoChange = (e) => {
    const valor = e.target.value.replace(/[^0-9+\s\-]/g, '');
    setFormData({...formData, conductor_telefono: valor});

    if (valor.replace(/[\s\-]/g, '').length >= 9) {
      const validacion = validarTelefono(valor);
      if (validacion.valido) {
        setTelefonoError('');
        setTelefonoTipo(validacion.tipo);
      } else {
        setTelefonoError(validacion.mensaje);
        setTelefonoTipo('');
      }
    } else {
      setTelefonoError('');
      setTelefonoTipo('');
    }
  };

  const handleCpChange = (e) => {
    const valor = e.target.value.replace(/\D/g, '').slice(0, 5);
    setFormData({...formData, conductor_codigo_postal: valor});
    if (valor.length === 5) {
      setCpError('');
    } else if (valor.length > 0) {
      setCpError('Debe tener 5 dígitos');
    } else {
      setCpError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validar DNI
    const validacionDni = validarDocumentoIdentidad(formData.conductor_dni);
    if (!validacionDni.valido) {
      setDniError(validacionDni.mensaje);
      return;
    }

    // Validar Email
    const validacionEmail = validarEmail(formData.conductor_email);
    if (!validacionEmail.valido) {
      setEmailError(validacionEmail.mensaje);
      return;
    }

    // Validar Teléfono
    const validacionTelefono = validarTelefono(formData.conductor_telefono);
    if (!validacionTelefono.valido) {
      setTelefonoError(validacionTelefono.mensaje);
      return;
    }

    // Validar dirección: requiere tipo_via + nombre_via
    if (!formData.conductor_tipo_via || !formData.conductor_nombre_via.trim()) {
      alert('El tipo de vía y nombre de vía son obligatorios');
      return;
    }

    // Validar código postal
    if (!/^\d{5}$/.test(formData.conductor_codigo_postal)) {
      setCpError('Código postal debe tener 5 dígitos');
      return;
    }

    // Validar población
    if (!formData.conductor_poblacion.trim()) {
      alert('La población del conductor es obligatoria');
      return;
    }

    // Validar provincia
    if (!formData.conductor_provincia) {
      alert('La provincia del conductor es obligatoria');
      return;
    }

    // Compose legacy fields for backward compatibility
    const direccionCompuesta = [
      formData.conductor_tipo_via,
      formData.conductor_nombre_via,
      formData.conductor_numero,
      formData.conductor_portal ? `Portal ${formData.conductor_portal}` : '',
      formData.conductor_escalera ? `Esc. ${formData.conductor_escalera}` : '',
      formData.conductor_planta,
      formData.conductor_puerta,
    ].filter(Boolean).join(' ');

    onSubmit({
      ...formData,
      conductor_direccion: direccionCompuesta,
      conductor_ciudad: formData.conductor_poblacion,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8">
        <h2 className="text-2xl font-bold mb-6">
          {isEditing ? 'Editar Asignación' : 'Nueva Asignación de Vehículo'}
        </h2>

        {!isEditing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              <strong>Nota:</strong> Las fechas de inicio y fin se registrarán automáticamente
              cuando el conductor firme la entrega y devolución del vehículo.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matrícula *
              </label>
              <input
                type="text"
                value={formData.matricula}
                onChange={(e) => setFormData({...formData, matricula: e.target.value.toUpperCase()})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="1234ABC"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                DNI/NIE/Pasaporte *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.conductor_dni}
                  onChange={handleDniChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                    dniError
                      ? 'border-red-500 focus:ring-red-500'
                      : dniTipo
                      ? 'border-green-500 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="12345678A, X1234567B o Pasaporte"
                  required
                />
                {dniTipo && (
                  <span className="absolute right-3 top-2.5 text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                    {dniTipo}
                  </span>
                )}
              </div>
              {dniError && (
                <p className="text-sm text-red-600 mt-1">{dniError}</p>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre *
              </label>
              <input
                type="text"
                value={formData.conductor_nombre}
                onChange={(e) => setFormData({...formData, conductor_nombre: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apellidos *
              </label>
              <input
                type="text"
                value={formData.conductor_apellidos}
                onChange={(e) => setFormData({...formData, conductor_apellidos: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email *
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={formData.conductor_email}
                  onChange={handleEmailChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                    emailError
                      ? 'border-red-500 focus:ring-red-500'
                      : emailValido
                      ? 'border-green-500 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="conductor@email.com"
                  required
                />
                {emailValido && (
                  <span className="absolute right-3 top-2.5 text-green-600">&#10003;</span>
                )}
              </div>
              {emailError && (
                <p className="text-sm text-red-600 mt-1">{emailError}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Teléfono *
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.conductor_telefono}
                  onChange={handleTelefonoChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                    telefonoError
                      ? 'border-red-500 focus:ring-red-500'
                      : telefonoTipo
                      ? 'border-green-500 focus:ring-green-500'
                      : 'border-gray-300 focus:ring-blue-500'
                  }`}
                  placeholder="612345678"
                  required
                />
                {telefonoTipo && (
                  <span className="absolute right-3 top-2.5 text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                    {telefonoTipo}
                  </span>
                )}
              </div>
              {telefonoError && (
                <p className="text-sm text-red-600 mt-1">{telefonoError}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4 mt-2">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Dirección del conductor</h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Vía *
                  </label>
                  <select
                    value={formData.conductor_tipo_via}
                    onChange={(e) => setFormData({...formData, conductor_tipo_via: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {TIPOS_VIA.map(tipo => (
                      <option key={tipo} value={tipo}>{tipo}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de Vía *
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_nombre_via}
                    onChange={(e) => setFormData({...formData, conductor_nombre_via: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Gran Vía"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-5 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_numero}
                    onChange={(e) => setFormData({...formData, conductor_numero: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="1"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Portal
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_portal}
                    onChange={(e) => setFormData({...formData, conductor_portal: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Escalera
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_escalera}
                    onChange={(e) => setFormData({...formData, conductor_escalera: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder=""
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Planta
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_planta}
                    onChange={(e) => setFormData({...formData, conductor_planta: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="2"
                    maxLength={10}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Puerta
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_puerta}
                    onChange={(e) => setFormData({...formData, conductor_puerta: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="A"
                    maxLength={10}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código Postal *
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_codigo_postal}
                    onChange={handleCpChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 ${
                      cpError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="28001"
                    maxLength={5}
                    required
                  />
                  {cpError && (
                    <p className="text-sm text-red-600 mt-1">{cpError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Población/Municipio *
                  </label>
                  <input
                    type="text"
                    value={formData.conductor_poblacion}
                    onChange={(e) => setFormData({...formData, conductor_poblacion: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Madrid"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provincia *
                  </label>
                  <select
                    value={formData.conductor_provincia}
                    onChange={(e) => setFormData({...formData, conductor_provincia: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Seleccionar...</option>
                    <option value="A Coruña">A Coruña</option>
                    <option value="Álava">Álava</option>
                    <option value="Albacete">Albacete</option>
                    <option value="Alicante">Alicante</option>
                    <option value="Almería">Almería</option>
                    <option value="Asturias">Asturias</option>
                    <option value="Ávila">Ávila</option>
                    <option value="Badajoz">Badajoz</option>
                    <option value="Barcelona">Barcelona</option>
                    <option value="Bizkaia">Bizkaia</option>
                    <option value="Burgos">Burgos</option>
                    <option value="Cáceres">Cáceres</option>
                    <option value="Cádiz">Cádiz</option>
                    <option value="Cantabria">Cantabria</option>
                    <option value="Castellón">Castellón</option>
                    <option value="Ceuta">Ceuta</option>
                    <option value="Ciudad Real">Ciudad Real</option>
                    <option value="Córdoba">Córdoba</option>
                    <option value="Cuenca">Cuenca</option>
                    <option value="Gipuzkoa">Gipuzkoa</option>
                    <option value="Girona">Girona</option>
                    <option value="Granada">Granada</option>
                    <option value="Guadalajara">Guadalajara</option>
                    <option value="Huelva">Huelva</option>
                    <option value="Huesca">Huesca</option>
                    <option value="Illes Balears">Illes Balears</option>
                    <option value="Jaén">Jaén</option>
                    <option value="La Rioja">La Rioja</option>
                    <option value="Las Palmas">Las Palmas</option>
                    <option value="León">León</option>
                    <option value="Lleida">Lleida</option>
                    <option value="Lugo">Lugo</option>
                    <option value="Madrid">Madrid</option>
                    <option value="Málaga">Málaga</option>
                    <option value="Melilla">Melilla</option>
                    <option value="Murcia">Murcia</option>
                    <option value="Navarra">Navarra</option>
                    <option value="Ourense">Ourense</option>
                    <option value="Palencia">Palencia</option>
                    <option value="Pontevedra">Pontevedra</option>
                    <option value="Salamanca">Salamanca</option>
                    <option value="Santa Cruz de Tenerife">Santa Cruz de Tenerife</option>
                    <option value="Segovia">Segovia</option>
                    <option value="Sevilla">Sevilla</option>
                    <option value="Soria">Soria</option>
                    <option value="Tarragona">Tarragona</option>
                    <option value="Teruel">Teruel</option>
                    <option value="Toledo">Toledo</option>
                    <option value="Valencia">Valencia</option>
                    <option value="Valladolid">Valladolid</option>
                    <option value="Zamora">Zamora</option>
                    <option value="Zaragoza">Zaragoza</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {isEditing ? 'Guardar Cambios' : 'Crear Asignación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Asignaciones;
